import { z } from 'zod'

/**
 * Cost-object tags (ADR-039; docs/refactor/projects/02 §3): the columns a cost document carries to say
 * which project (and optionally which plan node and cost category) it belongs to. The document's own
 * module owns and writes them after the neutral `CostObjectRegistry` validates them, so Purchasing and
 * Finance never import Projects. A tag means "cost on the project"; on a goods line of a purchase it
 * means "delivered to the project site" (no stock movement, ASM-196).
 */

const optionalId = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null)

/**
 * The three tag fields of a purchase line, a supplier allowance line or an expense. Omitted: no tag.
 * The plan node and the category need the project (`projectTagErrors`). The category, when left
 * empty, is resolved by the server from the company's mapping and stored with the document.
 */
export const projectTagFields = {
  projectId: optionalId,
  projectWbsNodeId: optionalId,
  projectCostCategoryId: optionalId,
}

export interface ProjectTagInput {
  projectId: string | null
  projectWbsNodeId: string | null
  projectCostCategoryId: string | null
}

/** A node or a category without a project is refused (the database refuses it too). */
export function projectTagErrors(tag: ProjectTagInput): { path: string; message: string }[] {
  if (tag.projectId !== null) return []
  const errors: { path: string; message: string }[] = []
  if (tag.projectWbsNodeId !== null) {
    errors.push({ path: 'projectWbsNodeId', message: 'Choose the project first' })
  }
  if (tag.projectCostCategoryId !== null) {
    errors.push({ path: 'projectCostCategoryId', message: 'Choose the project first' })
  }
  return errors
}

/** For `superRefine` on a schema that spreads `projectTagFields`. */
export function refineProjectTag(tag: ProjectTagInput, ctx: z.RefinementCtx): void {
  for (const e of projectTagErrors(tag)) ctx.addIssue({ code: 'custom', path: [e.path], message: e.message })
}

/** The tag as documents show it. Codes and names only (ASM-190): no amounts, dates or people. */
export interface CostObjectTag {
  project: { id: string; code: string; name: string; nameAr: string | null }
  /** Outline code and name of the plan node ("2.3 Finishes"); null: the project as a whole. */
  wbsNode: { id: string; outline: string; name: string; nameAr: string | null } | null
  costCategory: { id: string; code: string; name: string; nameAr: string | null } | null
}

/** A project as a document list's Project column shows it. */
export interface CostObjectProjectRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}
